import { useMemo, useState, useEffect } from 'react';
import { useDataOptional } from '../../contexts/DataContext';
import { normDueDate, normStartDate, normEndeavorId, normAssignmentId, normEventId } from '../../contexts/dataUtils';
import classifyAssessment, { assessmentSeverity, computeAssessmentKey } from './classifyAssessment';

/**
 * useUpcomingAssessments — gathers assessment-type items from both
 * assignments and events across all courses for the next 30 days.
 *
 * Returns: { assessments: AssessmentItem[], totalCount: number }
 *
 * Each AssessmentItem:
 *   { id, source ('assignment'|'event'), type (final/midterm/…),
 *     title, courseId, courseName, courseCode, date: Date,
 *     daysUntil: number, dateLabel: string,
 *     severity: number, raw: originalObject }
 */
export default function useUpcomingAssessments() {
  const dataContext = useDataOptional() || {};
  const state = dataContext?.state;
  const coursesById = state?.coursesById || {};
  const dataRefreshTrigger = dataContext?.dataRefreshTrigger || 0;

  // Sync with external mutation events (same pattern as useAcademicHealth)
  const [syncTick, setSyncTick] = useState(0);
  useEffect(() => {
    const bump = () => setSyncTick(t => t + 1);
    const events = [
      'assignments:updated',
      'scoutiq:assignmentUpdated',
      'scoutiq:eventUpdated',
      'scoutiq:forceRefresh',
    ];
    events.forEach(e => window.addEventListener(e, bump));
    return () => events.forEach(e => window.removeEventListener(e, bump));
  }, []);

  return useMemo(() => {
    if (!state) return { assessments: [], totalCount: 0 };

    const now = new Date();
    const in30d = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const candidates = [];



    // Helper: resolve course metadata
    const courseInfo = (courseId) => {
      if (!courseId) return { courseName: '', courseCode: '' };
      const c = coursesById[courseId] || coursesById[String(courseId)];
      if (!c) return { courseName: '', courseCode: '' };
      const name = c.name || c.title || '';
      // Derive code: first word or abbreviation
      const code = c.courseCode || c.code || abbreviate(name);
      return { courseName: name, courseCode: code };
    };

    // ── 1. Scan assignments ────────────────────────────────────────────────
    if (state.assignmentsById) {
      const seen = new Set();
      Object.values(state.assignmentsById).forEach(a => {
        if (!a || a.isDeleted) return;
        // Deduplicate
        const canonId = String(a.assignmentId ?? a.assignmentID ?? a.assignment_id ?? a.id ?? '');
        if (!canonId || seen.has(canonId)) return;
        seen.add(canonId);

        // Skip completed
        if (isCompleted(a)) return;

        const dueDate = normDueDate(a);
        if (!dueDate) return;
        const d = new Date(dueDate);
        if (d <= now || d > in30d) return;

        const title = a.title || a.name || '';
        const { isAssessment, type } = classifyAssessment(title);
        if (!isAssessment) return;

        const courseId = normEndeavorId(a);
        const { courseName, courseCode } = courseInfo(courseId);

        candidates.push({
          id: `a-${canonId}`,
          source: 'assignment',
          type,
          title,
          courseId,
          courseName,
          courseCode,
          date: d,
          daysUntil: diffDays(now, d),
          dateLabel: formatAbsDate(d),
          severity: assessmentSeverity(type),
          assignmentId: normAssignmentId(a),
          raw: a,
        });
      });
    }

    // ── 2. Scan events ─────────────────────────────────────────────────────
    if (state.eventsById) {
      const seen = new Set();
      Object.values(state.eventsById).forEach(e => {
        if (!e || e.isDeleted) return;
        const canonId = String(e.eventId ?? e.eventID ?? e.event_id ?? e.id ?? '');
        if (!canonId || seen.has(canonId)) return;
        seen.add(canonId);

        const start = normStartDate(e);
        if (!start) return;
        const d = new Date(start);
        if (d <= now || d > in30d) return;

        const title = e.title || e.name || '';
        const { isAssessment, type } = classifyAssessment(title);
        if (!isAssessment) return;

        const courseId = normEndeavorId(e);
        const { courseName, courseCode } = courseInfo(courseId);

        candidates.push({
          id: `e-${canonId}`,
          source: 'event',
          type,
          title,
          courseId,
          courseName,
          courseCode,
          date: d,
          daysUntil: diffDays(now, d),
          dateLabel: formatAbsDate(d),
          severity: assessmentSeverity(type),
          eventId: normEventId(e),
          raw: e,
        });
      });
    }

    // ── 3. Sort: soonest date, tie-break by severity (lower = higher priority)
    candidates.sort((a, b) => {
      const dateDiff = a.date - b.date;
      if (dateDiff !== 0) return dateDiff;
      return a.severity - b.severity;
    });

    // ── 4. Group into AssessmentGroups ───────────────────────────────────
    // Items sharing the same assessmentKey AND dates within ±24 h
    // merge into a single AssessmentGroup with an `items` array.
    const DAY_MS = 24 * 60 * 60 * 1000;
    const groupMap = new Map(); // assessmentKey → AssessmentGroup[]

    for (const item of candidates) {
      const key = computeAssessmentKey(item.courseId, item.type, item.title);

      if (!groupMap.has(key)) groupMap.set(key, []);
      const buckets = groupMap.get(key);

      // Try to merge into an existing bucket within ±24 h
      let merged = false;
      for (const bucket of buckets) {
        if (Math.abs(item.date - bucket.date) <= DAY_MS) {
          bucket.items.push(item);
          // Keep the earliest date as representative
          if (item.date < bucket.date) {
            bucket.date = item.date;
            bucket.daysUntil = item.daysUntil;
            bucket.dateLabel = item.dateLabel;
          }
          merged = true;
          break;
        }
      }

      if (!merged) {
        buckets.push({
          id: item.id,
          courseId: item.courseId,
          type: item.type,
          title: item.title,
          date: item.date,
          daysUntil: item.daysUntil,
          dateLabel: item.dateLabel,
          severity: item.severity,
          courseName: item.courseName,
          courseCode: item.courseCode,
          items: [item],
        });
      }
    }

    // Flatten buckets, pick best display title, re-sort
    const groups = [];
    for (const buckets of groupMap.values()) {
      for (const g of buckets) {
        // Prefer event title (usually cleanest); fall back to shortest title
        const eventItem = g.items.find(i => i.source === 'event');
        if (eventItem && eventItem.title.length <= 40) {
          g.title = eventItem.title;
        } else {
          g.title = g.items.reduce(
            (best, i) => (i.title.length < best.length ? i.title : best),
            g.items[0].title,
          );
        }
        g.id = g.items[0].id;
        groups.push(g);
      }
    }

    groups.sort((a, b) => {
      const dateDiff = a.date - b.date;
      if (dateDiff !== 0) return dateDiff;
      return a.severity - b.severity;
    });

    return { assessments: groups, totalCount: groups.length };
  }, [state, coursesById, dataRefreshTrigger, syncTick]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function isCompleted(a) {
  if (!a) return false;
  if (a.status === 'completed' || a.status === 'Completed') return true;
  if (a.completedAt != null || a.completed_at != null) return true;
  if (a.completed) return true;
  if (a.submissionStatus === 'Completed') return true;
  if (a.completionStatus === 'Completed') return true;
  return false;
}

function diffDays(from, to) {
  return Math.max(0, Math.ceil((to - from) / (24 * 60 * 60 * 1000)));
}

function formatAbsDate(d) {
  try {
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function abbreviate(name) {
  if (!name) return '';
  // Take first letters of words, max 4 chars
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return name.substring(0, 6).toUpperCase();
  return words.map(w => w[0]).join('').substring(0, 4).toUpperCase();
}

/** Normalize title for deduplication: lowercase, strip punctuation, collapse whitespace */
// eslint-disable-next-line no-unused-vars
function normalizeTitle(title) {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
